import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { breakpoints } from '../../environments/environment.breakpoints';
import { CtTableComponent } from '../ct-table/ct-table.component';
import { Color } from '../../enums/color.enum';

interface windowActionButton {
  icon: string;
  name : string;
  color : string;
  action : any;
  modelId : string;
  isVisible : boolean;
}

@Component({
  selector: 'lib-ct-button-compressed-container',
  templateUrl: './ct-button-compressed-container.component.html',
  styleUrls: ['./ct-button-compressed-container.component.css']
})
export class CtButtonCompressedContainerComponent extends CtrlBaseComponent implements OnInit, AfterViewInit {

  @ViewChild('buttonContainer', { read: ViewContainerRef }) buttonContainer: ViewContainerRef;

  //properties
  public windowActionButtons : windowActionButton[] = [];

  //when is table child
  //this variables contain id's of the child buttons of this component, when they are null it means every button will be shown
  //but if they contain id's only those buttons will be shown
  private xsVisibility: string;
  private smVisibility: string;
  private mdVisibility: string;
  private xsVisibilitySet : Set<string>;
  private smVisibilitySet : Set<string>;
  private mdVisibilitySet : Set<string>;
  public isTableChild : boolean = false;
  public tableParentComponent : CtTableComponent;
  public tableComponentId : string; //this property is automatically set by the table component when it paints this component
  public rowIndex : number; //this property is automatically set by the table component when it paints this component
  private collapsedColor: Color = Color.Default;
  private collapsedIcon: string = '';

  //util
  public isMediumSize : boolean = false;
  public isSmallSize : boolean = false;
  public isExtraSmall : boolean = false;
  public isWindowOpen : boolean = false;
  public ContainerColors = Color;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.manageWindowResizing(window.innerWidth);
    this.manageWindowActionButtons();
    this.hideButtonsByVisibilityConfig();
  }

  constructor(private cdf : ChangeDetectorRef) { 
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.setVisibilityToSets();
    this.setTableComponent();
    this.manageWindowResizing(window.innerWidth);
    this.manageButtonResize();
    this.hideButtonsByVisibilityConfig();
  }

  /**
   * The buttonContainer will show or hide the buttons depending on the visibility property of the buttonContainer metadata 
   */
  private setVisibilityToSets(): void {
    if(!this.tableComponentId) return;
    this.xsVisibilitySet = new Set<string>(this.xsVisibility.length === 0 ? null : this.xsVisibility.split(','));
    this.smVisibilitySet = new Set<string>(this.smVisibility.length === 0 ? null : this.smVisibility.split(','));
    this.mdVisibilitySet = new Set<string>(this.mdVisibility.length === 0 ? null : this.mdVisibility.split(','));
  }

  /**
   * When the buttonContainer is child of a table component, this method is called
   */
  private setTableComponent(): void {
    if(!this.getTableComponentId()) return;
    this.isTableChild = true;
    this.tableParentComponent = this.utilService.getComponentRef(this.getTableComponentId());
    this.setTableIdToButtons();
  }

  private setTableIdToButtons(): void {
    this.getMetaData().models.forEach((model : any) => {
      model.controlProperties['buttonContainerParentId'] = this.getId();
      if(model.controlActions.length > 0){
        model.controlActions[0].conditionParams.push(this.getTableComponentId());
      }
    });
  }

  private manageButtonResize(buttons : windowActionButton[] = null) {
    if((this.isMediumSize) || (this.isSmallSize && this.getMetaData().models.length === 1) || (this.isSmallSize && this.getTableComponentId())){
      this.paintChildren(this.getMetaData(), this.buttonContainer);
    }    
    if(!buttons){
      this.manageWindowActionButtons();
    }
  }

  public toggleActionWindow(): void {
    this.isWindowOpen = !this.isWindowOpen;
  }

  private manageWindowActionButtons() {
    if (!(this.isSmallSize || this.isExtraSmall && this.getMetaData().models.length >= 1)) return;
    if (this.getMetaData().models.length == null || this.getMetaData().models.length === 0) return;
    if (this.windowActionButtons.length > 0) return;
    this.getMetaData().models.forEach((model: any, index: number) => {

      let buttonText;

      if (model.controlProperties.buttonText === "null" || model.controlProperties.buttonText === '' || model.controlProperties.buttonText === null) {
        buttonText = model.controlProperties.description
      } else {
        buttonText = model.controlProperties.buttonText

      }
      this.windowActionButtons.push({
        icon: model.controlProperties.icon,
        name: buttonText,
        color: model.controlProperties.buttonColor,
        action: model.controlActions[0],
        modelId: model.controlProperties.modelId,
        isVisible: this.getIfButtonIsVisible(model.controlProperties.id)
      });
    });
  }

  private getIfButtonIsVisible(id: string): boolean {
    if (!this.isTableChild) return true;
    let visibilitySet: Set<string>;

    if (this.isExtraSmall) {
      visibilitySet = this.xsVisibilitySet;
    } else if (this.isSmallSize) {
      visibilitySet = this.smVisibilitySet;
    } else {
      visibilitySet = this.mdVisibilitySet;
    }

    if(visibilitySet.size === 0) return true;
    if(visibilitySet.has(id)) return true;
    return false;
  }
  
  private manageWindowResizing(width: number) {
    const wasMediumSize = this.isMediumSize;
    const wasSmallSize = this.isSmallSize;

    this.isSmallSize = width < breakpoints.L && width >= breakpoints.M;
    this.isExtraSmall = width >= breakpoints.S && width < breakpoints.M;
    this.isMediumSize = width >= breakpoints.L;
    this.cdf.detectChanges();
    this.checkIfPaintChildren(wasMediumSize, wasSmallSize);
  }

  private hideButtonsByVisibilityConfig(): void {
    if(!(this.isMediumSize || this.isSmallSize)) return;
    this.getChildElements().forEach((element : CtrlBaseComponent) => {
      if(!this.getIfButtonIsVisible(element.getId().replace(this.getViewId()+'_', ''))){
        element.setHidden(true);
      }
    });
  }

  private checkIfPaintChildren(wasMediumSize: boolean, wasSmallSize: boolean) {
    if(!wasMediumSize && this.isMediumSize){
        this.manageButtonResize();
    }
    if(!wasSmallSize && this.isSmallSize){
      this.manageButtonResize();
    }
  }

  public removeButtonsFromWindow(modelIds : string[]) {
    let newModels : any[] = [];
    this.windowActionButtons = this.windowActionButtons.map((button : any) => {
      if(modelIds.includes(button.modelId)){
        button = null;
      }
      return button;
    }).filter((button : any) => button != null);
    this.getMetaData().models.forEach(element => {
      if(!modelIds.includes(element.controlProperties.modelId)){
        newModels.push(element);
      }
    });
    this.getMetaData().models = newModels;
    this.cdf.detectChanges();
    this.manageButtonResize(this.windowActionButtons);
  }

  public executeButtonAction(index : number): void {
    const action = this.windowActionButtons[index].action;
    action.conditionParams = new Array(...new Set<string>([... action.conditionParams]));
    this.onButtonClick();
    if(action) this.checkConfirmation(action);
  }

  /**
   * This method is called when the button is clicked, before it executes the action of the button, this will be used to set the current selected row
   * in the table component, so when the CRUD or SERVICE action is executed, the table will know which data send to the API.
   */
  public onButtonClick(): void {
    if(this.isTableChild){
      this.tableParentComponent.setSelectedRowFromButtonContainer(this.rowIndex);
    }
  }
  
  public getXsVisibility(): string { return this.xsVisibility; }
  public getSmVisibility(): string { return this.smVisibility; }
  public getMdVisibility(): string { return this.mdVisibility; }
  public getTableComponentId(): string { return this.tableComponentId; }
  public getRowIndex(): number { return this.rowIndex; }
  public getCollapsedColor(): Color {return this.collapsedColor}
  public getCollapsedIcon(): string {return this.collapsedIcon}
  public setXsVisibility(value: string) { this.xsVisibility = value; }
  public setSmVisibility(value: string) { this.smVisibility = value; }
  public setMdVisibility(value: string) { this.mdVisibility = value; }
  public setTableComponentId(value: string) { this.tableComponentId = value; }
  public setRowIndex(value: number) { this.rowIndex = value; }
  public setCollapsedColor(collapsedColor : Color) { this.collapsedColor = collapsedColor; }
  public setCollapsedIcon(collapsedIcon: string) {this.collapsedIcon = collapsedIcon; }
} 
